import {action, autorun, observable} from 'mobx'
import {notification} from 'antd'

import {Single} from './single'
import {List} from './list'
import {Messages} from './messages'
import {Tables} from './tables'
import {Charts} from 'Store/charts'
import {Debug} from './debug'
import {TickerDetails} from './tickerDetails'

import {getLoadErrorMessage} from './consts/errorMessages'

export class BacktestStore {
  @observable single
  @observable charts
  @observable tickerDetails

  @observable completedPercentage = 0

  @observable selectedTickers = []
  @observable metrics = []
  @observable ticker_metrics = {}

  @observable ui = {
    pnlPopup: false
  }

  @observable editingAlgo = false
  @observable editingAlgoLoading = false
  @observable stockFilterLoading = false

  @observable algoSnapshot = {}

  constructor(rootStore, api) {
    this.root = rootStore
    this.api = api

    this.list = new List(this.root, this.api)
    this.debug = new Debug(this.root)
    this.setBlankData()
  }

  resetMetricsAndTables = autorun(() => {
    if (this.single && this.single.loading) {
      this.metrics = []
      this.tables = new Tables(this.root)
    }
  })

  changeFrequencyOnAlgoChange = autorun(() => {
    if (this.single && this.charts && this.single.algo.frequency !== this.charts.frequency) {
      this.charts.changeFrequency(this.single.algo.frequency)
    }
  })

  @action setBlankData() {
    this.single = new Single(this.root, this.api)
    this.messages = new Messages(this.root, this.api)
    this.tables = new Tables(this.root)
    this.charts = new Charts(this.root, 'backtests')
    this.tickerDetails = {}
    this.completedPercentage = 0
  }

  @action editAlgo = async () => {
    const {id} = this.single.algo

    this.editingAlgoLoading = true
    this.editingAlgo = true

    await this.root.algo.single.load(id)
    this.algoSnapshot = Object.assign({}, this.root.algo.single.data)
    this.editingAlgoLoading = false
  }

  @action selectTickers(tickers) {
    this.selectedTickers = tickers
  }

  @action createStockFilter = async () => {
    this.stockFilterLoading = true
    const tickers = this.selectedTickers.map(ticker => (
      {
        data: {},
        ticker: ticker
      }
    ))
    let body = {
      selected_tickers: {tickers: tickers},
      condition: {conditions: [], custom_logic: 'AND'}
    }
    this.root.stockFilter.single.blank()
    body = {...this.root.stockFilter.current, ...body}

    try {
      const {id} = await this.api.request('sys/stock-filter', 'post', body)
      this.root.redirectTo({
        pathname:'/builder/',
        query:{stock_filter:id}
      })
    } catch (e) {
      notification['error']({
        message: 'Failed to save stock filter',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.stockFilterLoading = false
    }
  }

  @action fetchMetrics = async (id, ticker) => {
    if (this.single.data.is_currently_running) {
      return setTimeout(() => this.fetchMetrics(id), 1000)
    }
    let url = `sys/backtests/${id}/metrics`
    if (ticker) {
      url += `?ticker=${ticker}`
    }

    try {
      const {metrics} = await this.api.request(url, 'get')
      if (ticker) {
        this.ticker_metrics[ticker] = await metrics.filter(row => row.ident !== 'MAX_DRAWDOWN_PERCENTAGE')
      } else {
        this.metrics = await metrics.filter(row => row.ident !== 'MAX_DRAWDOWN_PERCENTAGE')
      }
    } catch (e) {
      notification['error']({
        message: 'Failed to load backtest metrics',
        description: e.message || 'Unknown error'
      })
    }
  }

  @action updateAndRun = async () => {
    this.editingAlgoLoading = true
    const {id} = this.single.algo
    await this.root.algo.single.update(id)
    await this.checkUpdateAndRunStatus()
    this.editingAlgoLoading = false
    this.run()
  }

  @action checkUpdateAndRunStatus = async (id = this.single.data.id) => {
    const {status} = await this.api.request(`sys/backtests/${id}/status`, 'get')
    if (status !== 'running') {
      return
    } else {
      return this.checkUpdateAndRunStatus(id)
    }
  }

  @action run = async (id = this.single.data.id) => {
    this.tickerDetails = {}
    this.completedPercentage = 0
    const body = Object.assign({}, this.single.data)

    try {
      await this.api.request(`sys/backtests/${id}/run`, 'post', body)
      this.single.change('is_currently_running', true)
    } catch (e) {
      this.single.change('is_currently_running', false)
      notification['error']({
        description: getLoadErrorMessage(e.code)
      })
    } finally {
      this.metrics = []
      this.tables = new Tables(this.root)
    }
  }

  @action interrupt = async (id = this.single.data.id) => {
    this.interrupting = true
    try {
      await this.api.request(`sys/backtests/${id}/interrupt`, 'post')
      this.single.change('is_currently_running', false)
    } catch (e) {
      notification['error']({
        message: 'Failed to interrupt backtest',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.interrupting = false
    }
  }

  @action calculatePnlByTicker = async () => {
    this.single.change('pnl_by_ticker', true)
    return this.run()
  }

  @action getTickerData(ticker) {
    if (!this.tickerDetails[ticker]) {
      const dataObj = new TickerDetails(this.root)
      this.tickerDetails[ticker] = dataObj
      dataObj.setValues({ticker: ticker, id: this.single.data.id})
    }
  }

  @action checkStatus = async () => {
    if (!this.single.data.id) {
      return
    }
    try {
      const {completed_percentage, status} = await this.api.request(`sys/backtests/${this.single.data.id}/status`, 'get')
      let completedPercentage = Math.max(this.completedPercentage, completed_percentage, 0)
      if (!completedPercentage || isNaN(completedPercentage)) {
        completedPercentage = 0
      }
      this.completedPercentage = completedPercentage
      if (status !== 'running') {
        this.single.load(this.single.data.id)
        this.completedPercentage = 0
      } else {
        setTimeout(() => {
          this.checkStatus()
        }, 500)
      }
    } catch (e) {
      this.single.change('is_currently_running', false)
      notification['error']({
        message: 'Failed to load backtest status',
        description: e.message || 'Unknown error'
      })
    }
  }

  fetchStatus = autorun(() => {
    if (this.single && this.single.data.is_currently_running) {
      this.checkStatus()
    }
  })
}
